import { useEffect, useState } from 'react';
import { Modal, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type {
  PerkGroup,
  ResolvedPlatformProfile,
} from '../../domain/entities/catalog.entity';
import { usePerkHolders, perkGroupPerkIds } from '../hooks/use-perk-holders';
import { ScopeBadge } from '@/shared/scopes/ScopeBadge';
import { Pagination } from '@react-apps/design-system';

const PER_PAGE = 10;

/**
 * Modal "ver usuários com esta capacidade": portadores distintos e ativos de
 * uma linha de `Catálogo → Perks`, paginado. Os ids vêm do delivery e a
 * exibição (nome/e-mail/scope) do identity — ver `usePerkHolders`.
 */
export function PerkHoldersModal({
  group,
  onClose,
}: {
  group: PerkGroup | null;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  // Cada capacidade começa na primeira página — sem isso, abrir a linha B herda
  // a página em que a linha A ficou.
  const groupId = group ? `${group.type}:${group.key}` : null;
  useEffect(() => {
    setPage(1);
  }, [groupId]);

  const perkIds = group ? perkGroupPerkIds(group) : [];
  const { data, isLoading, isFetching } = usePerkHolders(
    perkIds,
    page,
    PER_PAGE,
  );

  const columns: ColumnDef<ResolvedPlatformProfile>[] = [
    {
      key: 'name',
      header: 'Usuário',
      render: (row) => (
        <span className="text-(--color-text-primary) text-sm">{row.name}</span>
      ),
    },
    {
      key: 'email',
      header: 'E-mail',
      render: (row) => (
        <span className="text-(--color-text-secondary) text-sm">
          {row.email ?? '—'}
        </span>
      ),
    },
    {
      key: 'scopeId',
      header: 'Scope',
      render: (row) => (row.scopeId ? <ScopeBadge id={row.scopeId} /> : '—'),
    },
  ];

  return (
    <Modal
      open={group !== null}
      onClose={onClose}
      title={group ? `Portadores de ${group.name ?? group.key}` : 'Portadores'}
      description={
        group
          ? `Perfis com esta capacidade ativa. ${data?.total ?? 0} no total.`
          : undefined
      }
      size="lg"
    >
      <div className="space-y-4">
        <Table
          columns={columns}
          data={data?.items ?? []}
          keyExtractor={(row) => row.platformProfileId}
          loading={isLoading || isFetching}
          emptyMessage="Nenhum portador ativo."
          striped
        />
        <Pagination
          page={page}
          total={data?.total ?? 0}
          perPage={PER_PAGE}
          loading={isLoading}
          label="usuário"
          onPageChange={setPage}
        />
      </div>
    </Modal>
  );
}
